
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { Mail, MapPin, ArrowRight, MessageSquare, CheckCircle2, Plus, Minus, Phone, Clock, ArrowUpRight, FileText, PhoneCall, Play } from 'lucide-react';
import { supabase } from '../../src/libs/supabase';
import { sendEmail } from '../../src/libs/sendEmail';

// --- FAQ COMPONENT (MATCHING MAIN SITE STYLE) ---
const ContactFAQ = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      id: "REQ_01",
      q: t('faq.req_items.REQ_01.question'),
      a: t('faq.req_items.REQ_01.answer')
    },
    {
      id: "REQ_02",
      q: t('faq.req_items.REQ_02.question'),
      a: t('faq.req_items.REQ_02.answer')
    },
    {
      id: "REQ_03",
      q: t('faq.req_items.REQ_03.question'),
      a: t('faq.req_items.REQ_03.answer')
    },
    {
      id: "REQ_04",
      q: t('faq.req_items.REQ_04.question'),
      a: t('faq.req_items.REQ_04.answer')
    }
  ];

  return (
    <div className="mt-32 border-t border-gray-200 pt-24">
      <Reveal>
        <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 tracking-tighter leading-none">
            {t('faq.title_line1')}<br />{t('faq.title_line2')}
          </h2>
          <div className="text-right mt-8 md:mt-0">
            <p className="text-gray-500 text-lg max-w-md">
              {t('faq.contact_subtitle')}
            </p>
          </div>
        </div>
      </Reveal>

      <div className="flex flex-col border-t border-gray-200">
        {faqs.map((faq, index) => (
          <Reveal key={index} delay={index * 0.1}>
            <div className={`group border-b border-gray-200 transition-colors duration-300 ${openIndex === index ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
              <button
                className="w-full flex items-start md:items-center justify-between py-10 px-4 md:px-8 text-left"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-12">
                  <span className="font-mono text-xs text-gray-400 uppercase tracking-widest">
                      // {faq.id}
                  </span>
                  <span className={`text-xl md:text-2xl font-bold transition-colors duration-300 ${openIndex === index ? 'text-clever-teal' : 'text-gray-900'}`}>
                    {faq.q}
                  </span>
                </div>

                <div className="ml-4 flex-shrink-0 text-gray-400 font-mono">
                  {openIndex === index ? <Minus size={20} /> : <Plus size={20} />}
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${openIndex === index ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}
              >
                <div className="px-4 md:px-8 pb-10 grid grid-cols-1 md:grid-cols-12">
                  <div className="hidden md:block md:col-span-2"></div> {/* Spacer aligning with ID */}
                  <div className="md:col-span-8">
                    <div className="text-lg leading-relaxed text-gray-600 font-light pl-4 border-l border-clever-teal/30">
                      {faq.a}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
};

export const ContactPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [formState, setFormState] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    topic: t('contact.form.topics.strat'),
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 1. Insert into Database
      const { error } = await supabase.from('contacts').insert([{
        name: formState.name,
        company: formState.company,
        email: formState.email,
        phone: formState.phone,
        message: `Topic: ${formState.topic}\n\n${formState.message}`
      }]);

      if (error) throw error;

      // 2. Send Notification (with language)
      sendEmail({
        email: formState.email,
        name: formState.name,
        subject: `New Contact: ${formState.topic}`,
        message: `Name: ${formState.name}\nCompany: ${formState.company}\nEmail: ${formState.email}\nPhone: ${formState.phone}\n\nMessage:\n${formState.message}`,
        lang: i18n.language
      }).catch(err => console.error(err));

      setFormState({ name: '', company: '', email: '', phone: '', topic: t('contact.form.topics.strat'), message: '' });
      setIsSuccess(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      alert(t('contact.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-24 font-sans">

      {/* HERO SECTION - CENTERED LIKE HOME */}
      <section className="relative pt-40 pb-40 overflow-hidden flex flex-col justify-start items-center bg-white border-b border-gray-100">
        {/* Engineering Grid Background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_50%,transparent,white)] pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-6 relative z-10 w-full text-center">
          <Reveal>
            <div className="flex flex-col items-center">
              <div className="inline-flex items-center gap-3 mb-10 opacity-0 animate-[fadeIn_1s_ease-out_forwards]">
                <span className="h-px w-8 bg-clever-teal"></span>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-clever-teal">
                  {t('contact.tagline')}
                </span>
                <span className="h-px w-8 bg-clever-teal"></span>
              </div>

              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-gray-900 mb-8 leading-[0.95]">
                {t('contact.title_line1')} <br />
                <span className="text-gray-900">{t('contact.title_line2')}</span>
              </h1>

              <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-4 font-light leading-relaxed">
                {t('contact.hero_subtitle')}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MAIN SPLIT CONTENT */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-12 gap-8 items-stretch">

          {/* LEFT: THE FORM (Navy Background) */}
          <div className="lg:col-span-7 flex flex-col">
            <Reveal delay={0.2} className="h-full">
              {isSuccess ? (
                <div className="bg-[#0B1121] border border-gray-800 p-12 md:p-16 text-center rounded-none h-full flex flex-col items-center justify-center">
                  <div className="w-20 h-20 bg-white/10 border-2 border-clever-teal text-clever-teal rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg backdrop-blur-sm">
                    <CheckCircle2 size={40} />
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-4 tracking-tight">{t('contact.form.success_title')}</h3>
                  <p className="text-gray-400 text-lg leading-relaxed mb-8 max-w-md mx-auto">
                    {t('contact.form.success_msg')}
                  </p>
                  <button
                    onClick={() => setIsSuccess(false)}
                    className="text-sm font-bold uppercase tracking-widest text-clever-teal hover:text-white border-b-2 border-clever-teal pb-1 transition-colors"
                  >
                    {t('contact.form.success_cta')}
                  </button>
                </div>
              ) : (
                <div className="bg-[#0B1121] p-8 md:p-12 rounded-none shadow-2xl shadow-indigo-900/10 border border-gray-800 relative overflow-hidden h-full flex flex-col justify-center">
                  {/* Background Accent */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-clever-teal/10 rounded-full blur-[80px] pointer-events-none translate-x-1/2 -translate-y-1/2"></div>

                  <div className="relative z-10">
                    <h3 className="text-4xl md:text-5xl font-bold text-white mb-12 tracking-tighter leading-[0.9]">
                      {t('contact.form.title')}<br />
                      <span className="text-gray-500">{t('contact.form.subtitle')}</span>
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-10">
                      <div className="grid md:grid-cols-2 gap-10">
                        <div className="group relative">
                          <input
                            required
                            className="peer w-full bg-transparent border-b border-gray-700 py-3 text-white font-medium focus:border-clever-teal focus:outline-none transition-colors placeholder-transparent"
                            placeholder={t('contact.form.name')}
                            id="name"
                            value={formState.name}
                            onChange={e => setFormState({ ...formState, name: e.target.value })}
                          />
                          <label htmlFor="name" className="absolute left-0 -top-3 text-xs font-bold uppercase tracking-widest text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-gray-500 peer-focus:-top-3 peer-focus:text-clever-teal">
                            {t('contact.form.name')}
                          </label>
                        </div>
                        <div className="group relative">
                          <input
                            required
                            className="peer w-full bg-transparent border-b border-gray-700 py-3 text-white font-medium focus:border-clever-teal focus:outline-none transition-colors placeholder-transparent"
                            placeholder={t('contact.form.company')}
                            id="company"
                            value={formState.company}
                            onChange={e => setFormState({ ...formState, company: e.target.value })}
                          />
                          <label htmlFor="company" className="absolute left-0 -top-3 text-xs font-bold uppercase tracking-widest text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-gray-500 peer-focus:-top-3 peer-focus:text-clever-teal">
                            {t('contact.form.company')}
                          </label>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-10">
                        <div className="group relative">
                          <input
                            required
                            type="email"
                            className="peer w-full bg-transparent border-b border-gray-700 py-3 text-white font-medium focus:border-clever-teal focus:outline-none transition-colors placeholder-transparent"
                            placeholder={t('contact.form.email')}
                            id="email"
                            value={formState.email}
                            onChange={e => setFormState({ ...formState, email: e.target.value })}
                          />
                          <label htmlFor="email" className="absolute left-0 -top-3 text-xs font-bold uppercase tracking-widest text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-gray-500 peer-focus:-top-3 peer-focus:text-clever-teal">
                            {t('contact.form.email')}
                          </label>
                        </div>
                        <div className="group relative">
                          <input
                            type="tel"
                            className="peer w-full bg-transparent border-b border-gray-700 py-3 text-white font-medium focus:border-clever-teal focus:outline-none transition-colors placeholder-transparent"
                            placeholder={t('contact.form.phone')}
                            id="phone"
                            value={formState.phone}
                            onChange={e => setFormState({ ...formState, phone: e.target.value })}
                          />
                          <label htmlFor="phone" className="absolute left-0 -top-3 text-xs font-bold uppercase tracking-widest text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-gray-500 peer-focus:-top-3 peer-focus:text-clever-teal">
                            {t('contact.form.phone')}
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-4">{t('contact.form.topic')}</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {[
                            { key: 'auto', label: t('contact.form.topics.auto') },
                            { key: 'chat', label: t('contact.form.topics.chat') },
                            { key: 'strat', label: t('contact.form.topics.strat') },
                            { key: 'other', label: t('contact.form.topics.other') }
                          ].map((item) => (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => setFormState({ ...formState, topic: item.label })}
                              className={`p-4 text-left text-sm border transition-all duration-200 hover:border-gray-500 ${formState.topic === item.label ? 'border-clever-teal bg-clever-teal text-white font-bold shadow-sm' : 'border-gray-700 text-gray-400 bg-white/5'}`}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="group relative">
                        <textarea
                          className="peer w-full bg-transparent border-b border-gray-700 py-3 text-white font-medium focus:border-clever-teal focus:outline-none transition-colors min-h-[100px] resize-y placeholder-transparent leading-relaxed"
                          placeholder={t('contact.form.message')}
                          id="message"
                          value={formState.message}
                          onChange={e => setFormState({ ...formState, message: e.target.value })}
                        />
                        <label htmlFor="message" className="absolute left-0 -top-3 text-xs font-bold uppercase tracking-widest text-gray-500 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-gray-500 peer-focus:-top-3 peer-focus:text-clever-teal">
                          {t('contact.form.message')}
                        </label>
                      </div>

                      <div className="pt-4">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="bg-white text-[#0B1121] px-10 py-5 font-bold uppercase tracking-widest text-xs hover:bg-gray-200 transition-all w-full md:w-auto flex items-center justify-center gap-3 shadow-xl transform active:scale-95 rounded-none"
                        >
                          {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                          {!isSubmitting && <ArrowRight size={16} />}
                        </button>
                        <p className="text-xs text-gray-500 mt-4 font-mono">
                          {t('contact.form.footer')}
                        </p>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </Reveal>
          </div>

          {/* RIGHT: INFO & STEPS (Stretches to match height) */}
          <div className="lg:col-span-5 flex flex-col">
            <Reveal delay={0.4} className="h-full">
              <div className="bg-gray-50 p-10 lg:p-12 h-full border border-gray-200 rounded-none flex flex-col justify-between">

                <div className="mb-12">
                  <h3 className="text-xs font-bold text-gray-400 mb-8 uppercase tracking-widest flex items-center gap-2">
                    <MapPin size={14} className="text-clever-teal" /> {t('contact.info.presence')}
                  </h3>

                  <div className="space-y-2 mb-8">
                    <p className="text-3xl font-bold text-gray-900 leading-tight">Wiedner Gürtel 13</p>
                    <p className="text-3xl font-bold text-gray-900 leading-tight">1100 Wien</p>
                    <p className="text-gray-600 text-lg">{t('contact.info.austria')}</p>
                  </div>

                  <div className="flex flex-col gap-6 pt-8 border-t border-gray-200">
                    <a href="mailto:office@cleverdigital.at" className="flex items-center gap-4 group">
                      <div className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-400 group-hover:text-clever-teal group-hover:border-clever-teal transition-colors shadow-sm">
                        <Mail size={20} />
                      </div>
                      <span className="text-gray-900 font-medium group-hover:underline decoration-clever-teal underline-offset-4 transition-all text-lg">office@cleverdigital.at</span>
                    </a>
                    <a href="tel:+4366493367227" className="flex items-center gap-4 group">
                      <div className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-400 group-hover:text-clever-teal group-hover:border-clever-teal transition-colors shadow-sm">
                        <Phone size={20} />
                      </div>
                      <span className="text-gray-900 font-medium group-hover:underline decoration-clever-teal underline-offset-4 transition-all text-lg">+43 664 933 67227</span>
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-400 shadow-sm">
                    <Clock size={20} />
                  </div>
                  <span className="text-gray-500 text-lg">{t('contact.info.mon_fri')}</span>
                </div>
              </div>

              <div className="bg-white border border-gray-100 p-8 rounded-none relative overflow-hidden group shadow-sm mt-auto">
                <div className="absolute top-0 right-0 w-24 h-24 bg-clever-teal/5 rounded-full blur-[40px] group-hover:bg-clever-teal/10 transition-colors"></div>

                <h4 className="font-bold text-lg mb-6 relative z-10 text-gray-900 flex items-center gap-2">
                  <ArrowUpRight className="text-clever-teal" /> {t('contact.next_steps.title')}
                </h4>

                <ul className="space-y-5 relative z-10">
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 mt-0.5">1</div>
                    <div>
                      <span className="font-bold block text-sm text-gray-900">{t('contact.next_steps.step1_title')}</span>
                      <span className="text-gray-500 text-sm leading-relaxed">{t('contact.next_steps.step1_desc')}</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 mt-0.5">2</div>
                    <div>
                      <span className="font-bold block text-sm text-gray-900">{t('contact.next_steps.step2_title')}</span>
                      <span className="text-gray-500 text-sm leading-relaxed">{t('contact.next_steps.step2_desc')}</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 mt-0.5">3</div>
                    <div>
                      <span className="font-bold block text-sm text-gray-900">{t('contact.next_steps.step3_title')}</span>
                      <span className="text-gray-500 text-sm leading-relaxed">{t('contact.next_steps.step3_desc')}</span>
                    </div>
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>

        </div>

        {/* FAQ SECTION */}
        <Reveal delay={0.6}>
          <ContactFAQ />
        </Reveal>
      </section>
    </div>
  );
};
